import { useState, type CSSProperties } from 'react'
import { apiError } from '../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { irrigationApi, type Controller, type IrrigationZone, type Schedule } from '../lib/api'

// ── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#2D6A2F',
  greenBright:'#4CAF50',
  red:       '#F87171',
  blue:      '#60A5FA',
  teal:      '#2DD4BF',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
  hover:     'var(--color-bg-hover)',
}

// ── Brand metadata ──────────────────────────────────────────────────────────
const CONTROLLER_BRANDS: { value: string; label: string; needs_base_url: boolean; needs_username?: boolean; stub?: boolean; note?: string }[] = [
  { value: 'opensprinkler', label: 'OpenSprinkler', needs_base_url: true },
  { value: 'rachio',        label: 'Rachio',        needs_base_url: false },
  { value: 'bhyve',         label: 'Orbit bHyve',  needs_base_url: false },
  { value: 'hunter_hpc400', label: 'Hunter HPC400', needs_base_url: true, needs_username: true },
  { value: 'hydrawise',     label: 'Hunter Hydrawise', needs_base_url: false },
  { value: 'rainbird',      label: 'Rain Bird (LNK WiFi)', needs_base_url: true },
  { value: 'torotempus',    label: 'Toro Tempus',   needs_base_url: false, stub: true,
    note: 'Toro Tempus has no third-party API — it’s Bluetooth-only (Tempus DC) or a closed Toro cloud (Tempus Air), so Grafty can’t connect to it from our servers. Use the Toro app directly.' },
]

const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmt12h(t: string) {
  if (!t) return '—'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${((h % 12) || 12)}:${String(m).padStart(2, '0')} ${ampm}`
}

function relTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 2) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

// ── Shared UI components ─────────────────────────────────────────────────────
function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, ...style }}>
      {children}
    </div>
  )
}

function Chip({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: color + '22', color,
    }}>
      {children}
    </span>
  )
}

function Btn({
  onClick, disabled, variant = 'default', small, children, style,
}: {
  onClick?: () => void; disabled?: boolean; variant?: 'default' | 'danger' | 'ghost' | 'green' | 'blue' | 'teal'
  small?: boolean; children: React.ReactNode; style?: React.CSSProperties
}) {
  const colors: Record<string, { bg: string; fg: string; border?: string }> = {
    default: { bg: C.amber, fg: 'var(--color-bg)' },
    green:   { bg: C.green, fg: C.text },
    blue:    { bg: C.blue,  fg: 'var(--color-bg)' },
    teal:    { bg: C.teal,  fg: 'var(--color-bg)' },
    danger:  { bg: C.red,   fg: 'var(--color-bg)' },
    ghost:   { bg: 'transparent', fg: C.textMuted, border: `1px solid ${C.border}` },
  }
  const s = colors[variant]
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: small ? '5px 10px' : '8px 16px',
        borderRadius: 8, fontSize: small ? 12 : 13, fontWeight: 600,
        background: disabled ? 'var(--color-bg-hover)' : s.bg,
        color: disabled ? C.textFaint : s.fg,
        border: s.border ?? 'none',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'opacity 0.12s',
        ...style,
      }}
    >
      {children}
    </button>
  )
}

function Input({ value, onChange, placeholder, type = 'text', style }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; style?: React.CSSProperties
}) {
  return (
    <input
      type={type} value={value} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      style={{
        background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7,
        color: C.text, fontSize: 13, padding: '8px 10px', outline: 'none',
        width: '100%', boxSizing: 'border-box', ...style,
      }}
    />
  )
}

function Spinner() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
      style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  )
}

// ── Add Controller Modal ────────────────────────────────────────────────────
function AddControllerModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [brand, setBrand] = useState('opensprinkler')
  const [name, setName]   = useState('')
  const [url, setUrl]     = useState('http://192.168.1.100')
  const [otcToken, setOtcToken] = useState('')
  const [deviceId, setDeviceId] = useState('')
  const [password, setPassword] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [cfAccessId, setCfAccessId] = useState('')
  const [cfAccessSecret, setCfAccessSecret] = useState('')
  const [error, setError]  = useState('')

  const qc = useQueryClient()
  const addMutation = useMutation({
    mutationFn: () => {
      const selectedBrand = CONTROLLER_BRANDS.find(b => b.value === brand)
      const payload: Record<string, string> = {
        type: brand,
        name: name || selectedBrand?.label || brand,
      }
      if (selectedBrand?.needs_base_url) {
        // Local controllers. OpenSprinkler: local IP OR (recommended for remote) an OTC
        // token + password. Hunter HPC400: local URL + username (→ device_id) + password.
        if (otcToken.trim()) payload.otc_token = otcToken.trim()
        if (url.trim()) payload.base_url = url.trim()
        if (selectedBrand?.needs_username && deviceId.trim()) payload.device_id = deviceId.trim()
        if (password) payload.password = password
        // Cloudflare Tunnel Access service token (optional) — secures a tunneled local controller.
        if (cfAccessId.trim()) payload.cf_access_id = cfAccessId.trim()
        if (cfAccessSecret.trim()) payload.cf_access_secret = cfAccessSecret.trim()
      } else {
        if (deviceId.trim()) payload.device_id = deviceId.trim()
        if (apiKey) payload.api_key = apiKey
      }
      return irrigationApi.addController(payload)
    },
    onSuccess: async (res) => {
      // Try to import zones from the controller right away
      try { await irrigationApi.importZones(res.data.id) } catch { /* best effort */ }
      qc.invalidateQueries({ queryKey: ['irrigation-controllers'] })
      qc.invalidateQueries({ queryKey: ['irrigation-zones'] })
      onAdded()
    },
    onError: (e) => setError(apiError(e, 'Failed to add controller')),
  })

  const selectedBrand = CONTROLLER_BRANDS.find(b => b.value === brand)

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 440,
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, fontFamily: '"Syne", system-ui', color: C.text, margin: '0 0 20px' }}>
          Add Irrigation Controller
        </h2>

        {/* Brand picker */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 6 }}>Controller Brand</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {CONTROLLER_BRANDS.map(b => (
              <button
                key={b.value}
                onClick={() => setBrand(b.value)}
                style={{
                  padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                  background: brand === b.value ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : C.card2,
                  color: brand === b.value ? C.amber : b.stub ? C.textFaint : C.textMuted,
                  border: `1px solid ${brand === b.value ? C.amber : C.border}`,
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                {b.label}
                {b.stub && <span style={{ fontSize: 10, display: 'block', color: C.textFaint }}>Coming soon</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>Display Name</label>
          <Input value={name} onChange={setName} placeholder={selectedBrand?.label ?? 'My Controller'} />
        </div>

        {/* Brand-specific fields */}
        {selectedBrand?.needs_base_url ? (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>
              Controller URL <span style={{ color: C.textFaint }}>(local IP or hostname)</span>
            </label>
            <Input value={url} onChange={setUrl} placeholder="http://192.168.1.100" />
            {brand === 'opensprinkler' && (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>
                  OTC token <span style={{ color: C.textFaint }}>(for remote access via OpenThings Cloud — recommended)</span>
                </label>
                <Input value={otcToken} onChange={setOtcToken} placeholder="from openthings.io/my-account" />
                <p style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>
                  A local IP can't be reached from Grafty's servers — set an OTC token in your OpenSprinkler (Settings → OTC) for remote run/import/status.
                </p>
              </div>
            )}
            {selectedBrand?.needs_username && (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>
                  Username <span style={{ color: C.textFaint }}>(default: admin)</span>
                </label>
                <Input value={deviceId} onChange={setDeviceId} placeholder="admin" />
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>Password</label>
              <Input value={password} onChange={setPassword} type="password" placeholder={brand === 'opensprinkler' ? 'Controller password (default: opendoor)' : 'Controller password'} />
            </div>
            {brand === 'hunter_hpc400' && (
              <p style={{ fontSize: 11, color: C.textFaint, marginTop: 6 }}>
                Hunter HPC400 is reached over your local network — its URL must be reachable from Grafty's servers (a public/forwarded address), not a private LAN IP.
              </p>
            )}
            {brand === 'rainbird' && (
              <p style={{ fontSize: 11, color: C.textFaint, marginTop: 6 }}>
                Rain Bird LNK WiFi module — enter the module's URL + your LNK app password. It's local-network only (no Rain Bird cloud), so use a Cloudflare Tunnel below for remote access. <strong>Beta:</strong> the Rain Bird protocol is reverse-engineered and not yet verified on a physical unit — test before relying on it.
              </p>
            )}
            {/* Cloudflare Tunnel (free reverse proxy for a local controller) */}
            <div style={{ marginTop: 12, padding: '10px 12px', background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                Connect via Cloudflare Tunnel <span style={{ color: C.textFaint, fontWeight: 400 }}>(optional, free)</span>
              </div>
              <p style={{ fontSize: 11, color: C.textFaint, margin: '0 0 8px' }}>
                Don't have a public address? Run <code>cloudflared</code> on any always-on device at the farm (a $35 Raspberry Pi, NAS, or your router) to expose this controller at a stable HTTPS URL — no port-forwarding. Put that URL above. To keep it private, lock the tunnel with a{' '}
                <a href="https://developers.cloudflare.com/cloudflare-one/identity/service-tokens/" target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>Cloudflare Access service token</a>{' '}and paste it here.
              </p>
              <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>Access Client ID <span style={{ color: C.textFaint }}>(optional)</span></label>
              <Input value={cfAccessId} onChange={setCfAccessId} placeholder="xxxxxxxx.access" />
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>Access Client Secret</label>
                <Input value={cfAccessSecret} onChange={setCfAccessSecret} type="password" placeholder="service token secret" />
              </div>
            </div>
          </div>
        ) : !selectedBrand?.stub ? (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>
              Device ID
            </label>
            <Input value={deviceId} onChange={setDeviceId} placeholder="Device or account ID" />
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 5 }}>API key</label>
              <Input value={apiKey} onChange={setApiKey} type="password" placeholder="Cloud API key" />
            </div>
            <p style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>
              Credentials are stored write-only — not visible after saving.
            </p>
          </div>
        ) : (
          <div style={{ padding: '14px 16px', background: C.card2, borderRadius: 8, marginBottom: 14 }}>
            <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>
              {selectedBrand?.note || `${selectedBrand?.label} integration is coming soon. You can save this controller now and it will be available when support lands.`}
            </p>
          </div>
        )}

        {error && (
          <p style={{ color: C.red, fontSize: 12, marginBottom: 12, padding: '8px 12px', background: C.red + '11', borderRadius: 7 }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn
            variant="green"
            disabled={addMutation.isPending || (!name && !selectedBrand)}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending ? <><Spinner /> Adding…</> : '+ Add Controller'}
          </Btn>
        </div>
      </div>
    </div>
  )
}

// ── Zone water & capacity panel (§114) ───────────────────────────────────────
const PIPE_SIZES: { v: number; label: string }[] = [
  { v: 0.5, label: '½"' }, { v: 0.75, label: '¾"' }, { v: 1, label: '1"' },
  { v: 1.25, label: '1¼"' }, { v: 1.5, label: '1½"' }, { v: 2, label: '2"' },
  { v: 3, label: '3"' }, { v: 4, label: '4"' },
]
function ZoneWaterPanel({ zone }: { zone: IrrigationZone }) {
  const qc = useQueryClient()
  const [runMin, setRunMin] = useState(20)
  const { data, isLoading } = useQuery({
    queryKey: ['zone-hydraulics', zone.id, runMin],
    queryFn: () => irrigationApi.zoneHydraulics(zone.id, runMin).then(r => r.data),
  })
  // Editable settings (seeded from server once loaded)
  const [pipe, setPipe] = useState<number | ''>('')
  const [mat, setMat] = useState('pvc')
  const [emit, setEmit] = useState<string>('')
  const [seeded, setSeeded] = useState(false)
  if (data && !seeded) {
    setSeeded(true)
    setPipe(data.input_pipe_size || '')
    setMat(data.input_pipe_material || 'pvc')
    setEmit(data.default_emitter_gpm ? String(data.default_emitter_gpm) : '')
  }
  const save = useMutation({
    mutationFn: () => irrigationApi.updateZone(zone.id, {
      input_pipe_size: pipe === '' ? undefined : Number(pipe),
      input_pipe_material: mat,
      default_emitter_gpm: emit === '' ? undefined : Number(emit),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zone-hydraulics', zone.id] }),
  })
  const setEmitter = useMutation({
    mutationFn: ({ plantId, gpm }: { plantId: string; gpm: number | null }) =>
      irrigationApi.setPlantEmitter(plantId, gpm),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zone-hydraulics', zone.id] }),
  })

  const inp: CSSProperties = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: '5px 8px', outline: 'none', width: 72 }
  const util = data?.utilization_pct ?? 0
  const barColor = data?.over_capacity ? C.red : util >= 80 ? '#F59E0B' : C.greenBright

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
      {/* Settings */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
        <label style={{ fontSize: 10, color: C.textFaint }}>Input pipe
          <select value={pipe} onChange={e => setPipe(e.target.value === '' ? '' : Number(e.target.value))} style={{ ...inp, display: 'block', width: 80, marginTop: 2 }}>
            <option value="">—</option>
            {PIPE_SIZES.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 10, color: C.textFaint }}>Material
          <select value={mat} onChange={e => setMat(e.target.value)} style={{ ...inp, display: 'block', width: 80, marginTop: 2 }}>
            <option value="pvc">PVC</option><option value="poly">Poly</option>
          </select>
        </label>
        <label style={{ fontSize: 10, color: C.textFaint }}>Emitter GPM
          <input type="number" step="0.1" min="0" value={emit} onChange={e => setEmit(e.target.value)} placeholder="e.g. 1.0" style={{ ...inp, display: 'block', marginTop: 2 }} />
        </label>
        <Btn variant="green" small disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</Btn>
      </div>

      {isLoading || !data ? (
        <div style={{ fontSize: 12, color: C.textMuted }}>Loading…</div>
      ) : (
        <>
          {/* Headline numbers */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {[
              ['Plants', String(data.plant_count)],
              ['Demand', `${data.total_demand_gpm} GPM`],
              ['Pipe max', data.pipe_max_gpm ? `${data.pipe_max_gpm} GPM` : '—'],
              ['Max plants', data.max_plants_supported ? String(data.max_plants_supported) : '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ flex: '1 1 70px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 8px' }}>
                <div style={{ fontSize: 10, color: C.textFaint }}>{l}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{v}</div>
              </div>
            ))}
          </div>
          {/* Run-time water use */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
            <span>Run for</span>
            <input type="number" min="1" value={runMin} onChange={e => setRunMin(Math.max(1, Number(e.target.value) || 1))} style={{ ...inp, width: 56 }} />
            <span>min → <strong style={{ color: C.text }}>{data.gallons_per_run.toLocaleString()} gal</strong> / run</span>
          </div>
          {/* Capacity bar */}
          {data.pipe_max_gpm > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ height: 8, background: C.card, borderRadius: 5, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                <div style={{ width: `${Math.min(util, 100)}%`, height: '100%', background: barColor, transition: 'width .2s' }} />
              </div>
              <div style={{ fontSize: 11, color: data.over_capacity ? C.red : C.textFaint, marginTop: 3 }}>
                {util}% of pipe capacity{data.over_capacity ? ' — over-subscribed' : ` · ${data.headroom_gpm} GPM headroom`}
              </div>
            </div>
          )}
          {(data.notes ?? []).map((n, i) => (
            <div key={i} style={{ fontSize: 11, color: data.over_capacity ? C.red : C.textMuted, marginBottom: 4 }}>⚠ {n}</div>
          ))}
          {/* Per-plant emitter overrides */}
          {data.plants.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ fontSize: 11, color: C.textMuted, cursor: 'pointer' }}>Per-plant emitter rates ({data.plants.length})</summary>
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
                {data.plants.map(p => (
                  <div key={p.plant_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <span style={{ flex: 1, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label || 'Plant'}</span>
                    <input
                      type="number" step="0.1" min="0"
                      defaultValue={p.emitter_gpm ?? ''}
                      placeholder={`${data.default_emitter_gpm || 0}`}
                      onBlur={e => {
                        const v = e.target.value.trim()
                        setEmitter.mutate({ plantId: p.plant_id, gpm: v === '' ? null : Number(v) })
                      }}
                      style={{ ...inp, width: 64 }}
                      title="Override GPM (blank = zone default)"
                    />
                    <span style={{ color: C.textFaint, fontSize: 10, width: 30 }}>GPM</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  )
}

// ── Zone Card ────────────────────────────────────────────────────────────────
function ZoneCard({
  zone,
  isRunning,
  onRun,
  onStop,
  lastRun,
}: {
  zone: IrrigationZone
  isRunning: boolean
  onRun: (zoneId: string, station: number, minutes: number) => void
  onStop: () => void
  lastRun?: string
}) {
  const [duration, setDuration] = useState(5)
  const [showWater, setShowWater] = useState(false)

  return (
    <div style={{
      background: isRunning ? C.green + '22' : C.card2,
      border: `1px solid ${isRunning ? C.greenBright : C.border}`,
      borderRadius: 10, padding: 14, transition: 'all 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{zone.name || `Zone ${zone.station_number}`}</div>
          <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>
            Station {zone.station_number}
            {lastRun && <span style={{ marginLeft: 8 }}>· Last run {relTime(lastRun)}</span>}
          </div>
        </div>
        {isRunning && (
          <Chip color={C.greenBright}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.greenBright, animation: 'pulse 1s ease-in-out infinite', display: 'inline-block' }} />
            Running
          </Chip>
        )}
        {!zone.is_active && !isRunning && (
          <Chip color={C.textFaint}>Inactive</Chip>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {isRunning ? (
          <Btn variant="danger" small onClick={onStop}>◼ Stop</Btn>
        ) : (
          <>
            <Btn
              variant="green" small
              disabled={!zone.is_active}
              onClick={() => onRun(zone.id, zone.station_number, duration)}
            >
              ▶ Run
            </Btn>
            <select
              value={duration}
              onChange={e => setDuration(Number(e.target.value))}
              style={{
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 6,
                color: C.textMuted, fontSize: 12, padding: '4px 8px', outline: 'none',
              }}
              aria-label="Duration in minutes"
            >
              {[2, 5, 10, 15, 20, 30, 45, 60].map(m => (
                <option key={m} value={m}>{m} min</option>
              ))}
            </select>
          </>
        )}
        <button
          onClick={() => setShowWater(s => !s)}
          style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: C.textMuted, fontSize: 12, cursor: 'pointer' }}
          aria-expanded={showWater}
        >
          💧 Water & capacity {showWater ? '▾' : '▸'}
        </button>
      </div>
      {showWater && <ZoneWaterPanel zone={zone} />}
    </div>
  )
}

// ── Schedule Row ─────────────────────────────────────────────────────────────
function ScheduleRow({ schedule, zones, onToggle }: {
  schedule: Schedule
  zones: IrrigationZone[]
  onToggle: (id: string, enabled: boolean) => void
}) {
  const zone = zones.find(z => z.id === schedule.zone_id)
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{schedule.name}</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
          {zone ? (zone.name || `Zone ${zone.station_number}`) : '—'}
          {' · '}
          {fmt12h(schedule.start_time)}
          {' · '}
          {schedule.duration_minutes} min
        </div>
        <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>
          {schedule.days_of_week.length === 7
            ? 'Every day'
            : schedule.days_of_week.map(d => DAYS_SHORT[d]).join(', ')}
        </div>
      </div>
      {/* Toggle */}
      <button
        onClick={() => onToggle(schedule.id, !schedule.is_enabled)}
        aria-label={schedule.is_enabled ? 'Disable schedule' : 'Enable schedule'}
        style={{
          width: 40, height: 22, borderRadius: 11,
          background: schedule.is_enabled ? C.greenBright : 'rgba(255,255,255,0.1)',
          border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0,
          transition: 'background 0.2s',
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: schedule.is_enabled ? 20 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s',
        }} />
      </button>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export function IrrigationPage() {
  const qc = useQueryClient()
  const [activeTab, setActiveTab]     = useState<'zones' | 'schedules'>('zones')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCtrl, setSelectedCtrl] = useState<string | null>(null)
  const [runningZone, setRunningZone]  = useState<string | null>(null)
  const [runMsg, setRunMsg]            = useState<{ zone: string; text: string; ok: boolean } | null>(null)

  // ── Data fetching ────────────────────────────────────────────────────────
  const { data: ctrlData, isLoading: ctrlLoading } = useQuery({
    queryKey: ['irrigation-controllers'],
    queryFn: () => irrigationApi.listControllers(),
  })
  const controllers: Controller[] = ctrlData?.data?.controllers ?? []

  const { data: zoneData, isLoading: zonesLoading } = useQuery({
    queryKey: ['irrigation-zones'],
    queryFn: () => irrigationApi.listZones(),
    enabled: controllers.length > 0,
  })
  const zones: IrrigationZone[] = zoneData?.data?.zones ?? []

  const { data: scheduleData } = useQuery({
    queryKey: ['irrigation-schedules'],
    queryFn: () => irrigationApi.listSchedules(),
    enabled: zones.length > 0,
  })
  const schedules: Schedule[] = scheduleData?.data?.schedules ?? []

  // Active controller (first by default)
  const activeCtrl = selectedCtrl
    ? controllers.find(c => c.id === selectedCtrl)
    : controllers[0]

  const ctrlZones = zones.filter(z => z.controller_id === activeCtrl?.id)

  // ── Mutations ───────────────────────────────────────────────────────────
  const runMutation = useMutation({
    mutationFn: ({ station, minutes }: { zoneId: string; station: number; minutes: number }) =>
      irrigationApi.manualRun(activeCtrl!.id, station, minutes),
    onMutate: ({ zoneId }) => setRunningZone(zoneId),
    onSuccess: (_, { zoneId, minutes }) => {
      const zone = zones.find(z => z.id === zoneId)
      setRunMsg({ zone: zoneId, text: `${zone?.name ?? `Zone ${zone?.station_number}`} running for ${minutes} min`, ok: true })
    },
    onError: (e, { zoneId }) => {
      setRunMsg({ zone: zoneId, text: apiError(e, 'Run failed'), ok: false })
    },
    onSettled: () => setRunningZone(null),
  })

  const stopMutation = useMutation({
    mutationFn: () => irrigationApi.stopAll(activeCtrl!.id),
    onSuccess: () => setRunMsg({ zone: '', text: 'All zones stopped', ok: true }),
  })

  const toggleScheduleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      irrigationApi.updateSchedule(id, { is_enabled: enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['irrigation-schedules'] }),
  })

  const importMutation = useMutation({
    mutationFn: (id: string) => irrigationApi.importZones(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['irrigation-zones'] })
      setRunMsg({ zone: '', text: `Imported ${res.data.imported} zones from controller`, ok: true })
    },
    onError: (e) => {
      setRunMsg({ zone: '', text: apiError(e, 'Import failed'), ok: false })
    },
  })

  const deleteCtrlMutation = useMutation({
    mutationFn: (id: string) => irrigationApi.deleteController(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['irrigation-controllers'] })
      qc.invalidateQueries({ queryKey: ['irrigation-zones'] })
      setSelectedCtrl(null)
    },
  })

  // ── Tab styles helper ────────────────────────────────────────────────────
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: active ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : 'transparent',
    color: active ? C.amber : C.textMuted,
    border: active ? `1px solid color-mix(in srgb, var(--color-primary) 27%, transparent)` : '1px solid transparent',
    cursor: 'pointer',
  })

  // ── Loading ──────────────────────────────────────────────────────────────
  if (ctrlLoading) {
    return (
      <main id="main-content" style={{ padding: 32, fontFamily: '"DM Sans", system-ui, sans-serif', background: C.bg, minHeight: '100dvh', color: C.text }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.textMuted }}>
          <Spinner /> Loading irrigation…
        </div>
      </main>
    )
  }

  // ── Empty state: no controllers ──────────────────────────────────────────
  if (controllers.length === 0) {
    return (
      <main id="main-content" style={{ padding: 32, fontFamily: '"DM Sans", system-ui, sans-serif', background: C.bg, minHeight: '100dvh', color: C.text }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui', margin: '0 0 4px' }}>Irrigation</h1>
            <p style={{ color: C.textMuted, fontSize: 14, margin: 0 }}>Manage zones, schedules, and manual runs.</p>
          </div>
          <Btn variant="green" onClick={() => setShowAddModal(true)}>+ Add Controller</Btn>
        </div>

        <Card style={{ maxWidth: 480, textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>💧</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 10px' }}>No controllers yet</h2>
          <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, margin: '0 0 24px' }}>
            Connect an irrigation controller to start managing zones. Grafty supports OpenSprinkler,
            Rachio, and more.
          </p>
          <Btn variant="green" onClick={() => setShowAddModal(true)}>+ Add Controller</Btn>
        </Card>

        {showAddModal && (
          <AddControllerModal
            onClose={() => setShowAddModal(false)}
            onAdded={() => setShowAddModal(false)}
          />
        )}
        <style>{`@keyframes spin { to { transform: rotate(360deg) } } @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }`}</style>
      </main>
    )
  }

  // ── Main view ────────────────────────────────────────────────────────────
  return (
    <main id="main-content" style={{ padding: 32, fontFamily: '"DM Sans", system-ui, sans-serif', background: C.bg, minHeight: '100dvh', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui', margin: '0 0 4px' }}>Irrigation</h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: 0 }}>
            {zones.length} zone{zones.length !== 1 ? 's' : ''} across {controllers.length} controller{controllers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="danger" small onClick={() => stopMutation.mutate()} disabled={stopMutation.isPending}>
            {stopMutation.isPending ? <Spinner /> : '◼'} Stop All
          </Btn>
          <Btn variant="green" small onClick={() => setShowAddModal(true)}>+ Controller</Btn>
        </div>
      </div>

      {/* Flash message */}
      {runMsg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: runMsg.ok ? C.greenBright + '18' : C.red + '18',
          color: runMsg.ok ? C.greenBright : C.red,
          border: `1px solid ${runMsg.ok ? C.greenBright : C.red}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{runMsg.text}</span>
          <button onClick={() => setRunMsg(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Controller selector (if multiple) */}
      {controllers.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {controllers.map(ctrl => (
            <button
              key={ctrl.id}
              onClick={() => setSelectedCtrl(ctrl.id)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                background: (activeCtrl?.id === ctrl.id) ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : C.card2,
                color: (activeCtrl?.id === ctrl.id) ? C.amber : C.textMuted,
                border: `1px solid ${(activeCtrl?.id === ctrl.id) ? 'color-mix(in srgb, var(--color-primary) 33%, transparent)' : C.border}`,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: ctrl.is_online ? C.greenBright : C.red, flexShrink: 0,
              }} />
              {ctrl.name}
            </button>
          ))}
        </div>
      )}

      {/* Controller info card */}
      {activeCtrl && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 28 }}>🌊</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{activeCtrl.name}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                    background: activeCtrl.is_online ? C.greenBright : C.red,
                  }} />
                  {activeCtrl.is_online ? 'Online' : 'Offline'}
                  <span style={{ color: C.border }}>·</span>
                  {CONTROLLER_BRANDS.find(b => b.value === activeCtrl.type)?.label ?? activeCtrl.type}
                  {activeCtrl.base_url && (
                    <><span style={{ color: C.border }}>·</span><code style={{ fontSize: 11, color: C.textFaint }}>{activeCtrl.base_url}</code></>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="ghost" small
                onClick={() => importMutation.mutate(activeCtrl.id)}
                disabled={importMutation.isPending}
              >
                {importMutation.isPending ? <><Spinner /> Importing…</> : '↓ Sync Zones'}
              </Btn>
              <Btn variant="ghost" small
                onClick={() => {
                  if (confirm(`Remove "${activeCtrl.name}" and all its zones?`)) {
                    deleteCtrlMutation.mutate(activeCtrl.id)
                  }
                }}
                style={{ color: C.red, borderColor: C.red + '44' }}
              >
                Remove
              </Btn>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        <button style={tabStyle(activeTab === 'zones')} onClick={() => setActiveTab('zones')}>
          Zones ({ctrlZones.length})
        </button>
        <button style={tabStyle(activeTab === 'schedules')} onClick={() => setActiveTab('schedules')}>
          Schedules ({schedules.filter(s => ctrlZones.some(z => z.id === s.zone_id)).length})
        </button>
      </div>

      {/* ── Zones tab ────────────────────────────────────────────────────── */}
      {activeTab === 'zones' && (
        <>
          {zonesLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.textMuted }}>
              <Spinner /> Loading zones…
            </div>
          ) : ctrlZones.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '36px 24px' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔧</div>
              <p style={{ color: C.textMuted, fontSize: 14, margin: '0 0 16px' }}>
                No zones found. Use <strong style={{ color: C.text }}>↓ Sync Zones</strong> to import zones from the controller.
              </p>
              <Btn variant="ghost" small
                onClick={() => importMutation.mutate(activeCtrl!.id)}
                disabled={importMutation.isPending}
              >
                {importMutation.isPending ? <><Spinner /> Importing…</> : '↓ Sync Zones from Controller'}
              </Btn>
            </Card>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                {ctrlZones.map(zone => (
                  <ZoneCard
                    key={zone.id}
                    zone={zone}
                    isRunning={runningZone === zone.id || runMutation.isPending && runMutation.variables?.zoneId === zone.id}
                    lastRun={undefined}
                    onRun={(zoneId, station, minutes) => runMutation.mutate({ zoneId, station, minutes })}
                    onStop={() => stopMutation.mutate()}
                  />
                ))}
              </div>

              {/* Undesignated zones note */}
              {zones.some(z => z.controller_id === activeCtrl?.id) && (
                <div style={{
                  marginTop: 20, padding: '12px 16px', borderRadius: 8,
                  background: C.blue + '11', border: `1px solid ${C.blue}33`,
                  fontSize: 13, color: C.textMuted,
                }}>
                  💡 <strong style={{ color: C.blue }}>Tip:</strong> Open the <a href="/map" style={{ color: C.blue }}>Farm Map</a> to draw zone boundaries and link them to areas of your farm. Undesignated zones appear in a panel on the map view.
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Schedules tab ────────────────────────────────────────────────── */}
      {activeTab === 'schedules' && (
        <>
          {schedules.filter(s => ctrlZones.some(z => z.id === s.zone_id)).length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '36px 24px' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
              <p style={{ color: C.textMuted, fontSize: 14, margin: 0 }}>
                No schedules configured for this controller's zones. Schedules are created on the controller itself and sync here automatically.
              </p>
            </Card>
          ) : (
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Schedule</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Active</span>
              </div>
              {schedules
                .filter(s => ctrlZones.some(z => z.id === s.zone_id))
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map(s => (
                  <ScheduleRow
                    key={s.id}
                    schedule={s}
                    zones={zones}
                    onToggle={(id, enabled) => toggleScheduleMutation.mutate({ id, enabled })}
                  />
                ))}
            </Card>
          )}
        </>
      )}

      {/* Add Controller Modal */}
      {showAddModal && (
        <AddControllerModal
          onClose={() => setShowAddModal(false)}
          onAdded={() => setShowAddModal(false)}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }
      `}</style>
    </main>
  )
}
